import { supabase } from '../auth/SupabaseClient';
import type { ExcalidrawElement } from '@excalidraw/element/types';
import type { AppState, BinaryFiles } from '@excalidraw/excalidraw/types';
import { serializeAsJSON } from '@excalidraw/excalidraw/data/json';

interface DrawingData {
  id?: string;
  user_id: string;
  title: string;
  data: any; // JSONB - Excalidraw scene data
  thumbnail?: string;
  is_public?: boolean;
}

/**
 * Save drawing to Supabase
 */
export const saveDrawingToSupabase = async (
  userId: string,
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  drawingId?: string,
  title: string = 'Untitled Drawing',
): Promise<{ id: string; error: any }> => {
  try {
    // Serialize the drawing data (returns JSON string, parse for JSONB storage)
    const serializedDataString = serializeAsJSON(elements, appState, files, 'database');
    const serializedData = JSON.parse(serializedDataString);

    const drawingData: DrawingData = {
      user_id: userId,
      title,
      data: serializedData,
      is_public: false,
    };

    if (drawingId) {
      // Update existing drawing
      const { data, error } = await supabase
        .from('drawings')
        .update(drawingData)
        .eq('id', drawingId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return { id: data.id, error: null };
    } else {
      // Create new drawing
      const { data, error } = await supabase
        .from('drawings')
        .insert(drawingData)
        .select()
        .single();

      if (error) throw error;
      return { id: data.id, error: null };
    }
  } catch (error: any) {
    console.error('Error saving drawing to Supabase:', error);
    return { id: '', error };
  }
};

/**
 * Load user drawings from Supabase
 */
export const loadUserDrawingsFromSupabase = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('drawings')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Error loading drawings from Supabase:', error);
    return { data: [], error };
  }
};

/**
 * Load a specific drawing from Supabase
 */
export const loadDrawingFromSupabase = async (drawingId: string, userId: string) => {
  try {
    const { data, error } = await supabase
      .from('drawings')
      .select('*')
      .eq('id', drawingId)
      .eq('user_id', userId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Error loading drawing from Supabase:', error);
    return { data: null, error };
  }
};

/**
 * Delete a drawing from Supabase
 */
export const deleteDrawingFromSupabase = async (drawingId: string, userId: string) => {
  try {
    const { error } = await supabase
      .from('drawings')
      .delete()
      .eq('id', drawingId)
      .eq('user_id', userId);

    if (error) throw error;
    return { error: null };
  } catch (error: any) {
    console.error('Error deleting drawing from Supabase:', error);
    return { error };
  }
};

